// v2.23 (owner 2026-10-30, docs/rencana/PECAH-SPK-V223.md): a split Jersey order gives the pieces per size for
// every design (table sizes × designs) instead of a player row per piece; the names come later ("Lengkapi nama
// & nomor"). Pure helpers (no imports, Jest-tested). The server checks everything again.

const n = (x) => parseInt(x, 10) || 0;
const desainBaris = (b) => n(b?.desain_ke) || 1;

// {"1": {S: 0, M: 0, …}, "2": {…}} for the designs in use, keeping the numbers already typed
export function tabelKosong(desain, ukuranList, lama = {}) {
  const out = {};
  desain.forEach((k) => {
    out[k] = {};
    ukuranList.forEach((u) => { out[k][u] = n(lama?.[k]?.[u]); });
  });
  return out;
}

// pieces of design k = the qty of its jersey lines (Bagian 2)
export function pcsDesain(variasi, k) {
  return variasi.filter((v) => desainBaris(v) === k).reduce((s, v) => s + n(v.qty), 0);
}

// the table counted from the player rows (a row belongs to the design of its variasi line)
export function tabelDariPemain(pemain, variasi, ukuranList, desain) {
  const out = tabelKosong(desain, ukuranList);
  pemain.forEach((p) => {
    const v = variasi[n(p.variasi_no) - 1];
    const k = v ? desainBaris(v) : desain[0];
    if (out[k] && p.ukuran) out[k][p.ukuran] = n(out[k][p.ukuran]) + 1;
  });
  return out;
}

// the order's size recap = the sum of the designs
export function totalTabel(tabel, ukuranList) {
  const out = {};
  ukuranList.forEach((u) => { out[u] = Object.values(tabel || {}).reduce((s, t) => s + n(t?.[u]), 0); });
  return out;
}

export const totalDesain = (tabel, k) => Object.values(tabel?.[k] || {}).reduce((s, x) => s + n(x), 0);

// designs whose table total is not the pieces of their jersey lines: [{desain, ada, harus}]
export function cekTabel(tabel, variasi, desain) {
  return desain.map((k) => ({ desain: k, ada: totalDesain(tabel, k), harus: pcsDesain(variasi, k) }))
    .filter((x) => x.ada !== x.harus);
}

// what the form sends: numbers only, designs in use only
export function tabelUntukKirim(tabel, desain, ukuranList) {
  const out = {};
  desain.forEach((k) => {
    out[String(k)] = {};
    ukuranList.forEach((u) => { out[String(k)][u] = n(tabel?.[k]?.[u]); });
  });
  return out;
}

export const PETUNJUK_PECAH =
  "Pesanan ini dipecah per desain: cukup isi JUMLAH PER UKURAN untuk setiap desain di Bagian 4. "
  + "Nama dan nomor boleh dikosongkan dan dilengkapi setelah ACC customer lewat tombol \"Lengkapi nama & nomor\" "
  + "di halaman pesanan (tanpa mengubah harga, tanpa ACC ulang).";

// "Lengkapi nama & nomor": one empty row per piece, sizes and variasi already set from the order
// (split order: from the table per design, distributed over that design's variasi lines in order; one design:
// from the size recap). When the order already has rows, those are used instead.
export function barisDariPesanan(js, ukuranList) {
  const variasi = js.variasi || [];
  const ada = js.pemain || [];
  if (ada.length) {
    return ada.map((p) => ({ nama: p.nama || "", nomor: p.nomor || "", ukuran: p.ukuran, lengan: p.lengan || "PENDEK",
      catatan: p.catatan || "", variasi_no: p.variasi_no || (variasi.length === 1 ? 1 : "") }));
  }
  const rows = [];
  const tabel = js.ukuran_per_desain;
  if (tabel && Object.keys(tabel).length) {
    Object.keys(tabel).map(Number).sort((a, b) => a - b).forEach((k) => {
      // the design's variasi lines, each taking its qty of the rows in size order
      const lines = variasi.map((v, i) => ({ no: i + 1, sisa: n(v.qty), desain: desainBaris(v) })).filter((l) => l.desain === k);
      let li = 0;
      const urut = [...ukuranList, ...Object.keys(tabel[k]).filter((u) => !ukuranList.includes(u))];
      urut.forEach((u) => {
        for (let c = 0; c < n(tabel[k][u]); c += 1) {
          while (li < lines.length && lines[li].sisa <= 0) li += 1;
          const line = lines[li] || lines[lines.length - 1];
          if (line) line.sisa -= 1;
          rows.push({ nama: "", nomor: "", ukuran: u, lengan: "PENDEK", catatan: "", variasi_no: line ? line.no : "" });
        }
      });
    });
    return rows;
  }
  const rekap = js.ukuran || {};
  const urut = [...ukuranList, ...Object.keys(rekap).filter((u) => !ukuranList.includes(u))];
  urut.forEach((u) => {
    for (let c = 0; c < n(rekap[u]); c += 1) rows.push({ nama: "", nomor: "", ukuran: u, lengan: "PENDEK", catatan: "", variasi_no: variasi.length === 1 ? 1 : "" });
  });
  return rows;
}

// pasted spreadsheet rows (Nama ⇥ Nomor ⇥ Ukuran ⇥ Lengan ⇥ Catatan) applied onto the prepared rows in order:
// a pasted size that differs from the prepared row is reported, never silently changed
export function tempelKeBaris(baris, teks) {
  const lines = String(teks || "").split(/\r?\n/).map((l) => l.replace(/\s+$/, "")).filter((l) => l.trim());
  const out = baris.map((b) => ({ ...b }));
  const beda = [];
  lines.slice(0, out.length).forEach((l, i) => {
    const [nama = "", nomor = "", ukuran = "", lengan = "", catatan = ""] = l.split("\t");
    out[i] = { ...out[i], nama: nama.trim(), nomor: nomor.trim(), catatan: catatan.trim() };
    const le = lengan.trim().toUpperCase();
    if (le === "PANJANG" || le === "PENDEK") out[i].lengan = le;
    const u = ukuran.trim().toUpperCase();
    if (u && u !== out[i].ukuran) beda.push(`baris ${i + 1}: ditempel ${u}, pesanan ${out[i].ukuran}`);
  });
  return { baris: out, dipakai: Math.min(lines.length, out.length), lebih: Math.max(0, lines.length - out.length), beda };
}
